import type { Column } from "@shared/types/data-table"
import type { NativeToken } from "@shared/types/native-token.types"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import {
  createStatusColumn,
  createTextColumn,
  createDateColumn,
} from "./column-factories"

export const NATIVE_TOKEN_COLUMNS: Record<string, Column<NativeToken>> = {
  status: createStatusColumn<NativeToken>("native-token"),
  tokenReference: createTextColumn<NativeToken>(
    "tokenReference",
    "Token Reference",
    170,
    "font-mono text-muted-foreground group-hover:text-foreground truncate"
  ),
  merchantName: createTextColumn<NativeToken>(
    "merchantName",
    "Merchant",
    140,
    "text-muted-foreground group-hover:text-foreground"
  ),
  cardLast4: createTextColumn<NativeToken>(
    "cardLast4",
    "Card Last 4",
    100,
    "font-mono text-muted-foreground group-hover:text-foreground"
  ),
  cardBrand: createTextColumn<NativeToken>(
    "cardBrand",
    "Card Brand",
    110,
    "text-muted-foreground group-hover:text-foreground"
  ),
  usageCount: createTextColumn<NativeToken>(
    "usageCount",
    "Usage Count",
    110,
    "tabular-nums text-muted-foreground group-hover:text-foreground",
    undefined,
    (value: unknown) => Number(value ?? 0).toLocaleString()
  ),
  tokenExpiry: createTextColumn<NativeToken>(
    "tokenExpiry",
    "Expires",
    100,
    "text-muted-foreground group-hover:text-foreground"
  ),
  tokenCreated: createDateColumn<NativeToken>("tokenCreated", "Created", 150),
  lastUsed: createDateColumn<NativeToken>("lastUsed", "Last Used", 150),
}

export const NATIVE_TOKEN_STATUS_CONFIG = {
  all: ["status", "tokenReference", "merchantName", "cardLast4", "cardBrand", "usageCount", "tokenExpiry", "tokenCreated", "lastUsed"],
} as const

export const getNativeTokenColumns = (status: string = 'all'): Column<NativeToken>[] => {
  const keys = NATIVE_TOKEN_STATUS_CONFIG[status as keyof typeof NATIVE_TOKEN_STATUS_CONFIG] || NATIVE_TOKEN_STATUS_CONFIG.all
  return keys.map(key => NATIVE_TOKEN_COLUMNS[key]).filter(Boolean)
}

export const getNativeTokenColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(NATIVE_TOKEN_STATUS_CONFIG[status as keyof typeof NATIVE_TOKEN_STATUS_CONFIG] || NATIVE_TOKEN_STATUS_CONFIG.all)
  return Object.entries(NATIVE_TOKEN_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ["status", "tokenReference"].includes(key),
    order: index,
  }))
}
